import { call, put, select } from 'typed-redux-saga'
import { AnyAction } from 'redux'

import services from '@services'
import { showError } from '@store/ui/error/actions'
import { setLastServiceResponse } from '@store/session/actions'
import { ErrorCodes, MarginErrorCodes } from '@utils/enums'
import {
  contratosSuccess,
  contratosError,
  contratosRequest,
  contratosElegiveis,
} from '@store/refinanciamento/contratos/actions'
import { ApplicationState } from '@store/types'
import { HttpStatus } from '@services/base'
import { logout } from '@utils/functions'
import { BridgeService } from '@services/bridge'
import { ErroType } from '@utils/types'

import { ContractsData, ContratosAPI, ContratosRequestPayload } from '../types'

export default function* getContratosSaga(action: AnyAction) {
  const payload = action.payload as ContratosRequestPayload
  try {
    const { accessToken } = yield* select((state: ApplicationState) => state.session)
    const { data } = yield* call(services.getContratosService, accessToken)
    yield put(
      setLastServiceResponse({
        code: data?.errorCode || 'OK',
        serviceName: 'getContratosService',
      }),
    )

    if (data?.errorCode === MarginErrorCodes.DISQUALIFIED_CROSS_CS001) {
      payload.semContratosCallback()
      return
    }

    const contractsData = data as ContractsData
    const novoObj = {
      elegivel: contractsData.hasEligibleContracts,
      contratos: contractsData.contracts.map(
        ({ categoryDescription: category, registrations }) => ({
          descricaoConvenio: category,
          listMatricula: registrations?.map(({ registration, contractItems }) => ({
            numeroMatricula: registration,
            listContrato: contractItems.map(
              ({
                contract,
                proposal,
                outstandingBalance,
                installmentAmount,
                productCode,
                categoryId,
                categoryDescription,
                agencyId,
                agencyDescription,
                eligibleLoanRefinancing,
              }) => ({
                numeroContrato: contract,
                numeroProposta: proposal,
                codProduto: productCode,
                valorQuitacao: outstandingBalance,
                orgaoId: agencyId,
                orgaoDesc: agencyDescription,
                convenioId: categoryId,
                convenioDesc: categoryDescription,
                valorProximaParcela: installmentAmount,
                indisponivel: !eligibleLoanRefinancing,
              }),
            ),
          })),
        }),
      ),
    } as ContratosAPI

    const hasEligibleContracts = Boolean(
      novoObj?.contratos?.some((convenio) =>
        convenio.listMatricula?.some((matricula) =>
          matricula?.listContrato?.some((contrato) => !contrato?.indisponivel),
        ),
      ),
    )

    const isEligibleForRefinancing = Boolean(novoObj?.elegivel && hasEligibleContracts)

    yield put(contratosElegiveis(isEligibleForRefinancing))
    yield put(contratosSuccess(novoObj?.contratos))

    if (!isEligibleForRefinancing) {
      payload.semContratosCallback()
      return
    }

    payload.successCallback()
  } catch (error) {
    yield put(setLastServiceResponse({ code: 'ERROR', serviceName: 'getContratosService' }))
    const erroTyped = error as ErroType
    if (BridgeService.isBrowser() && erroTyped.status === HttpStatus.UNAUTHORIZED) {
      yield put(
        showError({
          title: 'Sessão expirada',
          message: 'Efetue o login novamente.',
          actionTitle: 'Continuar',
          functionCallback: () => logout(),
        }),
      )
    } else if (erroTyped.status === HttpStatus.PRECONDITION_FAILED) {
      payload.semContratosCallback()
    } else {
      yield put(contratosError())
      yield put(
        showError({
          title: 'Erro ao buscar os contratos',
          message: 'Ocorreu um erro ao buscar a listagem de contratos.',
          actionTitle: 'Tentar Novamente',
          code: ErrorCodes.RE_001_CONTRATOS,
          actionCallback: () => contratosRequest(payload),
        }),
      )
    }
  }
}
